import { Databases } from 'appwrite';
import client from '../app-user';

const databases = new Databases(client);
const DATABASE_ID = process.env.REACT_APP_DATABASE_ID || 'database';
const LEAGUE_SETTINGS_COLLECTION_ID = process.env.REACT_APP_LEAGUE_SETTINGS_COLLECTION_ID || 'league-settings';
const SETTINGS_DOCUMENT_ID = 'current';

export const DEFAULT_SALARY_CAP = 188;
export type RFAStatus = 'blind_auction' | 'selection_process' | 'off';

export interface LeagueSettings {
  salaryCap: number;
  updatedBy?: string;
  rfaPage: RFAStatus;
}

export interface SeasonStatus {
  season: string;
  week: number | null;
  isInSeason: boolean;
  phase: 'preseason' | 'regular' | 'postseason' | 'offseason';
}

export const getLeagueSettings = async (): Promise<LeagueSettings> => {
  try {
    const document = await databases.getDocument(DATABASE_ID, LEAGUE_SETTINGS_COLLECTION_ID, SETTINGS_DOCUMENT_ID);
    const salaryCap = Number(document.salaryCap);
    const rfaPage: RFAStatus = ['blind_auction', 'selection_process'].includes(document.rfaPage)
      ? document.rfaPage
      : 'off';
    return { salaryCap: Number.isFinite(salaryCap) ? salaryCap : DEFAULT_SALARY_CAP, updatedBy: document.updatedBy, rfaPage };
  } catch (error) {
    console.info('League settings are unavailable; using the default salary cap.', error);
    return { salaryCap: DEFAULT_SALARY_CAP, rfaPage: 'off' };
  }
};

export const updateSalaryCap = async (salaryCap: number, updatedBy = ''): Promise<LeagueSettings> => {
  const payload = { salaryCap, updatedBy };
  try {
    const document = await databases.updateDocument(DATABASE_ID, LEAGUE_SETTINGS_COLLECTION_ID, SETTINGS_DOCUMENT_ID, payload);
    return { salaryCap: Number(document.salaryCap), updatedBy: document.updatedBy, rfaPage: document.rfaPage || 'off' };
  } catch (error) {
    if ((error as { code?: number }).code !== 404) throw error;
    const document = await databases.createDocument(DATABASE_ID, LEAGUE_SETTINGS_COLLECTION_ID, SETTINGS_DOCUMENT_ID, payload);
    return { salaryCap: Number(document.salaryCap), updatedBy: document.updatedBy, rfaPage: document.rfaPage || 'off' };
  }
};

export const updateRfaPage = async (rfaPage: RFAStatus, updatedBy = ''): Promise<RFAStatus> => {
  await databases.updateDocument(DATABASE_ID, LEAGUE_SETTINGS_COLLECTION_ID, SETTINGS_DOCUMENT_ID, { rfaPage, updatedBy });
  return rfaPage;
};

export const getSeasonStatus = async (): Promise<SeasonStatus> => {
  const response = await fetch('https://api.sleeper.app/v1/state/nfl');
  if (!response.ok) throw new Error(`Sleeper NFL state returned ${response.status}`);
  const state = await response.json() as { season: string; week: number; season_type: string };
  const phase = state.season_type === 'pre'
    ? 'preseason'
    : state.season_type === 'regular'
      ? 'regular'
      : state.season_type === 'post'
        ? 'postseason'
        : 'offseason';
  const isInSeason = phase !== 'offseason';
  return { season: state.season, week: isInSeason ? state.week : null, isInSeason, phase };
};
